import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  copyDocumentLink,
  duplicateDocument,
  hasSubpages,
  isPinned,
  togglePin,
} from '../../extensions/bundled/documents/documentsActions';
import { deleteNodes, listTree } from '../../extensions/bundled/documents/documentsStore';
import { bucketScopeOf } from '../../extensions/bundled/documents/documentsScopeTree';
import { collectSubtree } from '../../extensions/bundled/documents/documentsTree';
import { bumpDocumentsTree, useDocumentsUiStore } from '../../extensions/bundled/documents/documentsUiStore';
import { closeDocumentTab, openDocumentTab } from '../../store/tabsStore';
import { useNotificationStore } from '../../store/notificationStore';
import { notifyMcpSurfaceChanged } from '../../mcp/notify';
import { errorMessage } from '../../utils/errorMessage';
import { printDocument } from '../DocumentPrintView';
import { exportOpenDocument } from './exportDocument';

/**
 * What the toolbar's Share button and the more-menu do (cards 2.1, 3.1, 3.2),
 * on the store actions of card 3.4. Returns the handlers plus the state of
 * the three dialogs DocumentActionModals.js renders (duplicate with
 * sub-pages?, Move to…, delete with sub-pages).
 *
 * `flush` saves pending edits first, so a duplicate or an export never misses
 * the last keystrokes; `snapshot` is the live document.
 */
export function useDocumentActions({ documentId, scope, title, flush, snapshot }) {
  const { t } = useTranslation();
  const treeVersion = useDocumentsUiStore((s) => s.treeVersion);
  const [pinned, setPinned] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind: 'duplicate'|'move'|'delete', ... } | null
  const notes = useNotificationStore.getState;

  useEffect(() => {
    let cancelled = false;
    isPinned(documentId, bucketScopeOf(scope))
      .then((p) => {
        if (!cancelled) setPinned(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, treeVersion, scope.type, scope.teamId]);

  const fail = useCallback(
    (err, key) => notes().error(errorMessage(err, t(key)), t('docEditor.actions.failedTitle')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const runDuplicate = useCallback(
    async (includeSubpages) => {
      setDialog(null);
      try {
        await flush();
        const { id } = await duplicateDocument(documentId, scope, { includeSubpages });
        bumpDocumentsTree();
        openDocumentTab({ documentId: id, label: t('docStore.copyTitle', { title: title || t('docEditor.untitled') }), scope });
        notes().success(t('docEditor.actions.duplicated'), t('docEditor.actions.duplicatedTitle'));
      } catch (err) {
        // Plans v2: the upgrade modal already says why; no second error.
        if (err?.planLimit) return;
        fail(err, 'docEditor.actions.duplicateFailed');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId, scope, title, flush, fail]
  );

  const subtreeOf = async () => {
    const nodes = await listTree(bucketScopeOf(scope));
    return { nodes, subtree: collectSubtree(nodes, documentId) };
  };

  const performDelete = useCallback(
    async (subtree) => {
      setDialog(null);
      try {
        // Tabs first, as the sidebar does: their unmount flushes become
        // no-ops once deleteNodes marks the ids (deletedDocsGuard).
        for (const n of subtree) if (n.type === 'document') closeDocumentTab(n.id);
        await deleteNodes(subtree, scope);
        notifyMcpSurfaceChanged('documents');
        bumpDocumentsTree();
      } catch (err) {
        fail(err, 'docEditor.actions.deleteFailed');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, fail]
  );

  const onAction = useCallback(
    async (id) => {
      try {
        if (id === 'duplicate') {
          const { nodes } = await subtreeOf();
          if (hasSubpages(nodes, documentId)) setDialog({ kind: 'duplicate' });
          else await runDuplicate(false);
        } else if (id === 'move') {
          setDialog({ kind: 'move' });
        } else if (id === 'pin') {
          const next = await togglePin(documentId, bucketScopeOf(scope));
          setPinned(next);
          bumpDocumentsTree();
        } else if (id === 'print') {
          await printDocument(snapshot());
        } else if (id === 'copyLink') {
          await copyDocumentLink(documentId, scope);
        } else if (id === 'trash') {
          const { subtree } = await subtreeOf();
          const list = subtree.length ? subtree : [{ id: documentId, type: 'document' }];
          // Same rule as the sidebar: a page without sub-pages goes at once.
          if (list.length <= 1) await performDelete(list);
          else setDialog({ kind: 'delete', subtree: list });
        }
      } catch (err) {
        fail(err, id === 'print' ? 'docEditor.actions.printFailed' : 'docEditor.actions.failed');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId, scope, snapshot, runDuplicate, performDelete, fail]
  );

  const onExport = useCallback(
    async (format, includeSubpages) => {
      try {
        await flush();
        await exportOpenDocument(format, { documentId, scope, snapshot: snapshot(), includeSubpages });
      } catch (err) {
        fail(err, 'docEditor.actions.exportFailed');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId, scope, snapshot, flush, fail]
  );

  const onShare = useCallback(() => copyDocumentLink(documentId, scope), [documentId, scope]);

  return { pinned, dialog, setDialog, onAction, onExport, onShare, runDuplicate, performDelete };
}
